import { React } from 'Imports';
import { isEncompass } from '$Utilities/integrationPlatform';
import NavigationButtons from './NavigationButtons';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import { VideoEventResponse } from '$Generated/api';
import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { Location } from 'react-router-dom';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import VideoRecallDetailsSection from './VideoRecallDetailsSection';
import VideoEventDetailsSection from './VideoEventDetailsSection';
import { ConfigService } from '$State/ConfigFreezerService';
import ActionSection from './ActionSection';
import ScoreVideoEvent from './ScoreVideoEvent';
import FinalReview from './FinalReview';
import { NavigationPrompt } from '$Components/Shared/NavigationPrompt';
import { ConfirmDialog } from '$Components/Shared/ConfirmDialog';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { VideoEventFilterServiceMultiselect } from '$State/VideoEventFilterFreezerServiceMultiselect';
import { useAiOverlayContext } from '$State/AiOverlay/AiOverlayContext';

type NavigationBlockReason = 'unsavedChanges' | 'aiOverlayLoading' | null;

const styles = require('./VideoEventDetailsSidePanel.scss') as {
    mainContainerGeotab: string;
    mainContainerEncompass: string;
    navigationButtons: string;
    sectionContainerGeotab: string;
    sectionContainerEncompass: string;
    detailsSection: string;
};

interface VideoEventDetailsSidePanelProps {
    videoEvent: VideoEventResponse;
    isVideoRecall?: boolean;
    onSelectVideoEvent: (eventId: string, goBack: boolean, addHistoryState?: boolean) => void;
    onVideoEventUpdated: () => void;
    showLocationMapTab?: boolean;
    onLocationMapClick?: () => void;
    locationLinkUrl?: string;
}

const _VideoEventDetailsSidePanel = (props: VideoEventDetailsSidePanelProps & IVideoEventServiceInjectedProps) => {
    const [userCanEdit, setUserCanEdit] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
    const [blockReason, setBlockReason] = useState<NavigationBlockReason>(null);
    const isNavigatingProgrammatically = useRef(false);
    const deletedEventRef = useRef<{ displayId: string; position: number } | null>(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { aiOverlayLoading } = useAiOverlayContext();

    useEffect(() => {
        setUserCanEdit(canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo]));
    }, []);

    const handleDirtyStateChange = useCallback((isDirty: boolean) => {
        setHasUnsavedChanges(isDirty);
    }, []);

    const handleShouldBlock = useCallback((loc: Location): boolean => {
        if (isNavigatingProgrammatically.current) return true;
        setBlockReason(hasUnsavedChanges ? 'unsavedChanges' : 'aiOverlayLoading');
        setIsConfirmDialogOpen(true);
        setPendingNavigation(loc);
        return false;
    }, [hasUnsavedChanges]);

    const handleConfirmDiscard = useCallback(() => {
        const destination = pendingNavigation;
        setIsConfirmDialogOpen(false);
        if (blockReason === 'unsavedChanges') {
            setHasUnsavedChanges(false);
        }
        setPendingNavigation(null);
        setBlockReason(null);
        if (destination) {
            isNavigatingProgrammatically.current = true;
            navigate(
                { pathname: destination.pathname, search: destination.search, hash: destination.hash },
                { state: destination.state, replace: true },
            );
            isNavigatingProgrammatically.current = false;
        }
    }, [pendingNavigation, blockReason, navigate]);

    const handleCancelNavigation = useCallback(() => {
        setIsConfirmDialogOpen(false);
        setPendingNavigation(null);
        setBlockReason(null);
    }, []);

    const handleOnPromoted = () => {
        const displayId = props.videoEvent.displayId!;
        const eventUrl = ConfigService.isRunningGeotabIntegrated() ? ',eventId:' : '?eventId=';
        const rawPath = location.pathname + location.hash;
        const basePathWithoutEvent = rawPath.includes(eventUrl) ? rawPath.split(eventUrl)[0] : rawPath;
        const basePath = basePathWithoutEvent.replace(/videorecall/i, 'VideoEvents');
        // Clear the recall navigation list so the Prev/Next buttons are disabled once we land on
        // the promoted event's Video Event Details page (the recall list is no longer relevant here).
        props.videoEvents.clearListOfEventIds();
        navigate(basePath, { replace: true });
        navigate(`${basePath}${eventUrl}'${displayId}'`, { state: { eventId: displayId } });
    };

    const handleOnDeleted = () => {
        const eventUrl = ConfigService.isRunningGeotabIntegrated() ? ',eventId:' : '?eventId=';
        const rawPath = location.pathname + location.hash;
        const basePath = rawPath.includes(eventUrl) ? rawPath.split(eventUrl)[0] : rawPath;

        const { listofEventIdResult } = props.videoEvents.getState();
        const allIds = listofEventIdResult?.data?.videoEventDisplayIdList ?? [];

        const deletedDisplayId = props.videoEvent.displayId ?? '';
        const position = allIds.indexOf(deletedDisplayId);
        const nextEventId = position !== -1 && position < allIds.length - 1 ? allIds[position + 1] : undefined;

        deletedEventRef.current = position !== -1 ? { displayId: deletedDisplayId, position } : null;
        props.videoEvents.removeEventIdFromNavigationList(deletedDisplayId);

        if (nextEventId) {
            props.onSelectVideoEvent(nextEventId, false, false);
        } else {
            navigate(basePath, { replace: true });
        }
    };

    const handleOnUndoDelete = () => {
        const deletedDisplayId = props.videoEvent.displayId ?? '';

        if (deletedEventRef.current?.displayId === deletedDisplayId) {
            props.videoEvents.insertEventIdToNavigationList(deletedDisplayId, deletedEventRef.current.position);
            deletedEventRef.current = null;
        }

        props.onSelectVideoEvent(deletedDisplayId, false, false);
        const videoEventFilter = VideoEventFilterServiceMultiselect.getVideoEventFilter();
        VideoEventService.getListOfEventIds(videoEventFilter);
    };

    return (
        <>
            <NavigationPrompt when={hasUnsavedChanges || aiOverlayLoading} shouldBlock={handleShouldBlock} />

            <div className={isEncompass ? styles.mainContainerEncompass : styles.mainContainerGeotab}>
                <div className={styles.navigationButtons}>
                    <NavigationButtons
                        eventDisplayId={props.videoEvent?.displayId}
                        isVideoRecall={props.isVideoRecall}
                        onSelectVideoEvent={props.onSelectVideoEvent}
                    />
                </div>

                <div className={isEncompass ? styles.sectionContainerEncompass : styles.sectionContainerGeotab}>
                    <div className={styles.detailsSection}>
                        {props.isVideoRecall ? (
                            <VideoRecallDetailsSection
                                videoEvent={props.videoEvent}
                                onPromoted={handleOnPromoted}
                                onDeleted={handleOnDeleted}
                            />
                        ) : (
                            <VideoEventDetailsSection
                                videoEvent={props.videoEvent}
                                userCanEdit={userCanEdit}
                                onVideoEventUpdated={props.onVideoEventUpdated}
                                onDelete={handleOnDeleted}
                                onUndoDelete={handleOnUndoDelete}
                                showLocationMapTab={props.showLocationMapTab}
                                onLocationClick={props.onLocationMapClick}
                                locationLinkUrl={props.locationLinkUrl}
                            />
                        )}
                    </div>
                </div>

                {!props.isVideoRecall && (
                    <>
                        <div className={isEncompass ? styles.sectionContainerEncompass : styles.sectionContainerGeotab}>
                            <ActionSection
                                title="Score Event"
                                description="Upon saving, score will be added or subtracted from the driver’s overall score (if applicable)."
                            >
                                <ScoreVideoEvent
                                    videoEvent={props.videoEvent}
                                    userCanEdit={userCanEdit}
                                    onVideoEventUpdated={props.onVideoEventUpdated}
                                    onDirtyStateChange={handleDirtyStateChange}
                                />
                            </ActionSection>
                        </div>
                        <div className={isEncompass ? styles.sectionContainerEncompass : styles.sectionContainerGeotab}>
                            <ActionSection title="Final Review" description="Select an action to complete this review.">
                                <FinalReview
                                    videoEvent={props.videoEvent}
                                    key={props.videoEvent.id}
                                    userCanEdit={userCanEdit}
                                    onVideoEventUpdated={props.onVideoEventUpdated}
                                />
                            </ActionSection>
                        </div>
                    </>
                )}
            </div>

            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                title={blockReason === 'aiOverlayLoading' ? 'Are you sure?' : isEncompass ? 'Are you sure?' : 'Unsaved Changes'}
                contentMessage={
                    blockReason === 'aiOverlayLoading'
                        ? 'Are you sure you want to leave? AI overlay is loading.'
                        : 'Are you sure you want to ' + (isEncompass ? 'exit' : 'leave') + ' without saving? All of your changes will be lost.'
                }
                cancelButton={isEncompass ? 'No, Go Back' : 'Cancel'}
                acceptButton={isEncompass ? 'Yes, Exit' : 'Leave'}
                handleModalClosed={handleCancelNavigation}
                handleSubmit={handleConfirmDiscard}
                isAlert={isEncompass}
                alertType={AlertTypes.Alert}
                showIcon={isEncompass}
                isButtonAlert={isEncompass}
                maxWidth={isEncompass ? '504px' : '400px'}
            />
        </>
    );
};

const VideoEventDetailsSidePanel = VideoEventService.inject(_VideoEventDetailsSidePanel);

export default VideoEventDetailsSidePanel;
